import type { GridFilterModel } from '../lib/dataGrid';
import { defineStoredJson } from '../utils/storage';

// ---------------------------------------------------------------------------
/**
 * Where a grid was left THIS VISIT: what it was filtered to, and how far it was
 * scrolled. The transient counterpart of gridPreferences (the curated column
 * layout), and deliberately a different lane:
 *
 * - sessionStorage, not localStorage. A filter is an ACTION, not a setting —
 *   coming back weeks later to silently filtered rows reads as data loss. A tab
 *   session is the span in which the user still knows what they filtered for.
 * - A RELOAD forgets the filters and keeps the offsets (see
 *   clearGridSessionFiltersOnReload). Only one of the two hides rows, and F5 is
 *   the gesture people make when something looks wrong.
 *
 * It exists because the list a dialog was opened from does NOT always stay
 * mounted underneath it. Its own grid page does (the editors are child routes),
 * but a record reached from ELSEWHERE — a chip on the Befundliste, an
 * Auswertung row — mounts a different page under the dialog, and closing it
 * rebuilds the list from scratch. So does a reload on an open editor's URL.
 */
// ---------------------------------------------------------------------------

export interface GridSessionState {
    /** The filter model as the grid exported it, quick-filter terms included. */
    filterModel?: GridFilterModel;
    scrollTop?: number;
}

type Store = Record<string, GridSessionState>;

// Renamed from `ast:gridSession` when every key in the app moved to one prefix.
const sessionStore = defineStoredJson<Store>({
    key: 'grid_session',
    area: 'session',
    legacyKeys: ['ast:gridSession'],
    fallback: () => ({}),
    // Corrupt or foreign storage must never take a grid down.
    decode: (parsed) =>
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Store)
            : undefined
});

function readStore(): Store {
    return sessionStore.read();
}

function writeStore(store: Store): void {
    // Private mode / quota — the list simply opens at the top, unfiltered.
    sessionStore.write(store);
}

/**
 * The key one grid's state is kept under. A grid id alone would not do: the
 * record grids are per project and the same id serves all of them, so the
 * Befunde of one Maßnahme would open scrolled to where its neighbour was left.
 */
export function gridSessionKey(
    gridId: string,
    projectId: string | undefined
): string {
    return `${gridId}:${projectId ?? ''}`;
}

/** Where this grid was left, or an empty state when there is nothing (usable). */
export function loadGridSession(key: string): GridSessionState {
    return readStore()[key] ?? {};
}

/** Update one half of a grid's remembered state, leaving the other as it was. */
export function saveGridSession(key: string, patch: GridSessionState): void {
    const store = readStore();
    store[key] = { ...(store[key] ?? {}), ...patch };
    writeStore(store);
}

/** Whether a filter model actually filters anything (see useGridSessionState). */
export function isFiltering(model: GridFilterModel | undefined): boolean {
    if (!model) {
        return false;
    }
    return (
        model.items.some((item) => item.value !== undefined) ||
        (model.quickFilterValues?.length ?? 0) > 0
    );
}

/**
 * Forget every grid's remembered FILTER when the page was reloaded, keeping the
 * scroll offsets.
 *
 * Called once at start-up, not per grid: a reload is a fact about the page, so
 * it settles the question for every list at once — a grid first opened an hour
 * later in the same page must not still be answering for that F5.
 *
 * Only the filter, because only the filter HIDES rows, and it is cheap to undo
 * (type the term again). Restoring the scroll offset is what a browser does for
 * any ordinary page anyway.
 */
export function clearGridSessionFiltersOnReload(): void {
    if (!wasReloaded()) {
        return;
    }
    const store = readStore();
    let changed = false;
    for (const [key, state] of Object.entries(store)) {
        if (!state.filterModel) {
            continue;
        }
        const kept = { ...state };
        delete kept.filterModel;
        store[key] = kept;
        changed = true;
    }
    if (changed) {
        writeStore(store);
    }
}

/**
 * Whether this page was RELOADED rather than navigated to. A missing or
 * unsupported timing entry counts as "not reloaded": keeping what was stored is
 * the harmless answer.
 */
function wasReloaded(): boolean {
    const [entry] = performance.getEntriesByType(
        'navigation'
    ) as PerformanceNavigationTiming[];
    return entry?.type === 'reload';
}
